import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import { IS_MIX_CATALOG } from "@/config/browserBoard";
import { normalizeArchiveRequestPage } from "@/features/archive/archivePagination";
import type { ArchiveThreadsResponse } from "@/features/archive/types";
import {
  type SearchScope,
  serializeSearchScope,
} from "@/features/search/searchScope";
import { useMixCandidateSlugs } from "@/features/settings/hooks/useMixCandidateSlugs";
import {
  resolveEnabledMixSlugs,
  useMixStore,
} from "@/features/settings/stores/mixStore";
import { apiGet } from "@/shared/api";
import { boardApiPath } from "@/shared/api/boardPath";

const ARCHIVE_PAGE_LIMIT = 100;

export function getArchiveThreadsPath(
  page: number,
  limit: number,
  query: string = "",
  scope: SearchScope = "all",
): string {
  const normalizedPage = normalizeArchiveRequestPage(page);
  const normalizedLimit = Math.max(1, Math.min(100, Math.floor(limit)));
  const normalizedQuery = query.trim().slice(0, 100);
  const search = new URLSearchParams({
    page: String(normalizedPage),
    limit: String(normalizedLimit),
  });
  if (normalizedQuery) search.set("q", normalizedQuery);
  const serializedScope = serializeSearchScope(scope);
  if (normalizedQuery && serializedScope) {
    search.set("scope", serializedScope);
  }
  return `${boardApiPath("/archive")}?${search.toString()}`;
}

/** 選択中の板をまとめて取得する総合過去ログAPIのpath。 */
export function getArchiveMixThreadsPath(
  slugs: readonly string[],
  page: number,
  limit: number,
  query: string = "",
  scope: SearchScope = "all",
): string {
  const normalizedPage = normalizeArchiveRequestPage(page);
  const normalizedLimit = Math.max(1, Math.min(100, Math.floor(limit)));
  const boards = slugs.join(",");
  const normalizedQuery = query.trim().slice(0, 100);
  const search = new URLSearchParams({
    page: String(normalizedPage),
    limit: String(normalizedLimit),
    b: boards,
  });
  if (normalizedQuery) search.set("q", normalizedQuery);
  const serializedScope = serializeSearchScope(scope);
  if (normalizedQuery && serializedScope) {
    search.set("scope", serializedScope);
  }
  return `/archive-mix?${search.toString()}`;
}

export function useArchiveThreads(
  page: number,
  limit: number = ARCHIVE_PAGE_LIMIT,
  query: string = "",
  scope: SearchScope = "all",
): UseQueryResult<ArchiveThreadsResponse> {
  const mixBoardSettings = useMixStore((state) => state.boardSettings);
  const candidateSlugs = useMixCandidateSlugs();
  const boardRevision = useMixStore((state) => state.boardRevision);
  const normalizedPage = normalizeArchiveRequestPage(page);
  const normalizedLimit = Math.max(1, Math.min(100, Math.floor(limit)));
  const mixSlugs = IS_MIX_CATALOG
    ? resolveEnabledMixSlugs(candidateSlugs, mixBoardSettings)
    : [];
  const path = IS_MIX_CATALOG
    ? getArchiveMixThreadsPath(
        mixSlugs,
        normalizedPage,
        normalizedLimit,
        query,
        scope,
      )
    : getArchiveThreadsPath(normalizedPage, normalizedLimit, query, scope);
  const isSearching = query.trim().length > 0;

  return useQuery({
    queryKey: [
      "archive-threads",
      path,
      ...(IS_MIX_CATALOG ? [boardRevision] : []),
    ],
    queryFn: ({ signal }) =>
      IS_MIX_CATALOG && mixSlugs.length === 0
        ? Promise.resolve<ArchiveThreadsResponse>({
            threads: [],
            pagination: {
              page: normalizedPage,
              limit: normalizedLimit,
              total: 0,
              totalPages: 0,
            },
            storage: {
              usedBytes: 0,
              limitBytes: 0,
              remainingBytes: 0,
              usageRatio: 0,
            },
          })
        : apiGet<ArchiveThreadsResponse>(path, {
            ...(IS_MIX_CATALOG || isSearching ? { cache: "no-store" } : {}),
            signal,
          }),
    refetchOnWindowFocus: true,
  });
}
